import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Dropzone from 'react-dropzone';
import { withNamespaces } from 'react-i18next';
import {
    RiBoxingLine,
    RiBuildingLine,
    RiErrorWarningLine,
    RiFileList3Line,
    RiLinkUnlink,
    RiQuestionLine,
} from 'react-icons/ri';
import { toast } from 'react-toastify';
import {
    Alert as ReactstrapAlert,
    Button,
    Card,
    CardBody,
    Col,
    Label,
    Row,
    Spinner,
    UncontrolledTooltip,
} from 'reactstrap';

import DocumentManagementService, {
    getDocumentApiError,
} from 'src/modules/3rd-party-management/apis/admin/DocumentManagementService';
import {
    DOCUMENT_FILE_EXTENSIONS,
    DOCUMENT_FILE_MAX_BYTES,
} from 'src/modules/3rd-party-management/constants';
import bottyCustomizeIcon from 'src/modules/3rd-party-management/assets/images/ai-onboarding/botty-customize.svg';

import Alert from '../../../alert';

const BATCH_SIZE = 5;
const STATUS_POLL_MS = 3000;
const TERMINAL_STATUSES = ['completed', 'completed_with_errors', 'expired'];
const STATUS_QUERY_KEY = '3rd-party-management-document-bulk-upload-status';
const ACCEPTED_FILE_TYPES = DOCUMENT_FILE_EXTENSIONS
    .map((extension) => `.${extension}`)
    .join(',');

const FILE_STATUS_LABELS = {
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed',
};

const MATCH_TYPE_LABELS = {
    supplier: 'Supplier',
    product: 'Product',
    supplier_and_product: 'Supplier and product',
    unlinked: 'Unlinked',
};

const SUMMARY_CARDS = [
    {
        key: 'storedDocuments',
        title: 'Stored documents',
        icon: RiFileList3Line,
        iconBackgroundColor: '#E8F8F3',
        iconColor: '#1CBB8C',
    },
    {
        key: 'failed',
        title: 'Failed files',
        icon: RiErrorWarningLine,
        iconBackgroundColor: '#FFD8D8',
        iconColor: '#822626',
    },
    {
        key: 'linkedToSupplier',
        title: 'Linked to a supplier',
        icon: RiBuildingLine,
        iconBackgroundColor: '#D9E9FF',
        iconColor: '#2F80ED',
    },
    {
        key: 'linkedToProduct',
        title: 'Linked to a product',
        icon: RiBoxingLine,
        iconBackgroundColor: '#EDE9FE',
        iconColor: '#7C3AED',
    },
    {
        key: 'unlinked',
        title: 'Unlinked',
        icon: RiLinkUnlink,
        iconBackgroundColor: '#FFF4E5',
        iconColor: '#F2994A',
    },
    {
        key: 'ambiguous',
        title: 'Ambiguous',
        icon: RiQuestionLine,
        iconBackgroundColor: '#FFE4D9',
        iconColor: '#FF763F',
    },
];

const isTerminalStatus = (status) => TERMINAL_STATUSES.includes(status);

const isLinkingStatus = (status) => !status || status === 'pending' || status === 'processing';

const fileExtension = (fileName) => fileName.split('.').pop()?.toLowerCase();

const fileKey = (file, index) => file.fileId || file.localKey || `${file.filename}-${index}`;

const ImportDocuments = ({
    t,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked,
}) => {
    const queryClient = useQueryClient();
    const sessionStarted = useRef(false);
    const statusErrorShown = useRef(false);
    const [jobId, setJobId] = useState(null);
    const [acceptedFiles, setAcceptedFiles] = useState([]);
    const [rejectedFiles, setRejectedFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [isStarting, setIsStarting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [isFinalized, setIsFinalized] = useState(false);
    const [startError, setStartError] = useState('');

    const statusQuery = useQuery({
        queryKey: [STATUS_QUERY_KEY, jobId],
        queryFn: async () => {
            const service = DocumentManagementService.getInstance();

            return await service.getBulkUploadStatus(jobId);
        },
        enabled: Boolean(jobId) && isFinalized,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        refetchInterval: (data) => (
            isTerminalStatus(data?.status) ? false : STATUS_POLL_MS
        ),
        onError: (error) => {
            if (statusErrorShown.current) {
                return;
            }

            statusErrorShown.current = true;
            toast(getDocumentApiError(error).message || t('An error occurred while retrieving the documents.'), {
                type: 'error',
            });
        },
    });

    const session = isFinalized ? statusQuery.data : null;
    const sessionStatus = session?.status;
    const isExpired = sessionStatus === 'expired';
    const isFinished = sessionStatus === 'completed' || sessionStatus === 'completed_with_errors';
    const isProcessing = isFinalized && !isTerminalStatus(sessionStatus);
    const canChooseFiles = Boolean(jobId) && !isFinalized && !isStarting && !isExpired;
    const summary = session?.summary;
    const resultFiles = session?.files || [];
    const hasAttentionItems = Number(summary?.failed || 0) > 0
        || Number(summary?.unlinked || 0) > 0
        || Number(summary?.ambiguous || 0) > 0;

    const startSession = useCallback(async () => {
        setIsStarting(true);
        setStartError('');
        setAcceptedFiles([]);
        setRejectedFiles([]);
        setUploadingFiles([]);
        setIsFinalized(false);
        setJobId(null);
        statusErrorShown.current = false;

        try {
            const service = DocumentManagementService.getInstance();
            const started = await service.startBulkUpload();

            setJobId(started?.jobId || null);

            if (!started?.jobId) {
                setStartError(t('The upload session could not be started.'));
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            setStartError(
                getDocumentApiError(error).message || t('The upload session could not be started.')
            );
        } finally {
            setIsStarting(false);
        }
    }, [t]);

    useEffect(() => {
        if (sessionStarted.current) {
            return;
        }

        sessionStarted.current = true;
        startSession();
    }, [startSession]);

    const rejectFileLocally = (file) => {
        const extension = fileExtension(file.name);

        if (extension === 'zip') {
            return t('ZIP files are not accepted.');
        }

        if (!DOCUMENT_FILE_EXTENSIONS.includes(extension)) {
            return t('Please upload a valid file');
        }

        if (file.size > DOCUMENT_FILE_MAX_BYTES) {
            return t('The file must be 20 MB or smaller.');
        }

        return '';
    };

    const handleFilesDropped = (selectedFiles, rejectedDropzoneFiles = []) => {
        const dropzoneRejected = rejectedDropzoneFiles.map(({ file }) => ({
            filename: file.name,
            formattedSize: file.size,
            message: fileExtension(file.name) === 'zip'
                ? t('ZIP files are not accepted.')
                : t('Please upload a valid file'),
        }));

        if (dropzoneRejected.length > 0) {
            setRejectedFiles((currentFiles) => [...currentFiles, ...dropzoneRejected]);
        }

        uploadFiles(selectedFiles);
    };

    const uploadFiles = async (selectedFiles) => {
        if (!jobId || selectedFiles.length === 0 || isUploading) {
            return;
        }

        const localRejected = [];
        const filesToUpload = [];

        selectedFiles.forEach((file) => {
            const message = rejectFileLocally(file);

            if (message) {
                localRejected.push({
                    filename: file.name,
                    message,
                    formattedSize: file.size,
                });

                return;
            }

            filesToUpload.push(file);
        });

        setRejectedFiles((currentFiles) => [...currentFiles, ...localRejected]);

        if (filesToUpload.length === 0) {
            return;
        }

        setUploadingFiles(filesToUpload.map((file) => ({
            localKey: `${file.name}-${file.size}-${file.lastModified}`,
            filename: file.name,
            formattedSize: file.size,
            status: 'uploading',
        })));
        setIsUploading(true);

        try {
            const service = DocumentManagementService.getInstance();
            const sizeByName = new Map(filesToUpload.map((file) => [file.name, file.size]));

            for (let index = 0; index < filesToUpload.length; index += BATCH_SIZE) {
                const batch = filesToUpload.slice(index, index + BATCH_SIZE);
                const result = await service.uploadBulkFiles(jobId, batch);

                setAcceptedFiles((currentFiles) => [
                    ...currentFiles,
                    ...(result?.accepted || []).map((file) => ({
                        ...file,
                        formattedSize: sizeByName.get(file.filename),
                    })),
                ]);
                setRejectedFiles((currentFiles) => [
                    ...currentFiles,
                    ...(result?.rejected || []).map((file) => ({
                        ...file,
                        formattedSize: sizeByName.get(file.filename),
                    })),
                ]);
                setUploadingFiles((currentFiles) => currentFiles.filter((file) => (
                    !batch.some((batchFile) => batchFile.name === file.filename)
                )));
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(getDocumentApiError(error).message || t('The files could not be uploaded.'), {
                type: 'error',
            });
        } finally {
            setUploadingFiles([]);
            setIsUploading(false);
        }
    };

    const handleFinalize = async () => {
        if (!jobId || isUploading || acceptedFiles.length === 0) {
            return;
        }

        setIsFinalizing(true);

        try {
            const service = DocumentManagementService.getInstance();
            const result = await service.finalizeBulkUpload(jobId);

            queryClient.setQueryData([STATUS_QUERY_KEY, jobId], result);
            setIsFinalized(true);
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(getDocumentApiError(error).message || t('The upload could not be finalized. You can try again.'), {
                type: 'error',
            });
        } finally {
            setIsFinalizing(false);
        }
    };

    const handleStartAgain = () => {
        sessionStarted.current = true;
        startSession();
    };

    const renderFileCard = (file, index, state) => (
        <Card
            className="mb-2 shadow-none dz-processing dz-image-preview dz-success dz-complete"
            key={fileKey(file, index)}
        >
            <div className="p-3">
                <Row className="align-items-center">
                    <Col className="col-auto">
                        <span
                            className="avatar-sm rounded bg-light d-inline-flex align-items-center justify-content-center"
                            style={{ width: 80, height: 80 }}
                        >
                            <i className="ri-file-text-line font-size-24" />
                        </span>
                    </Col>
                    <Col>
                        <span className="file-name">{file.filename}</span>
                        <p className="upload-details-container mb-0">
                            {file.formattedSize ? (
                                <span className="file-size">
                                    {(file.formattedSize / 1024).toFixed(2)} KB
                                </span>
                            ) : null}
                            {state === 'uploaded' && (
                                <span className="upload-status-message succeed">
                                    {t('File(s) successfully uploaded.')}
                                </span>
                            )}
                            {state === 'failed' && (
                                <span className="upload-status-message failed">
                                    {file.message || t('File(s) failed to upload.')}
                                </span>
                            )}
                        </p>
                    </Col>
                    <Col xs={2} className="actions-container text-end">
                        {state === 'uploading' && (
                            <Spinner size="sm" color="primary" />
                        )}
                    </Col>
                </Row>
            </div>
        </Card>
    );

    const renderUploader = () => (
        <div className="upload-files-items">
            <Row className="mb-4">
                <Col sm="12">
                    <Label className="form-label">
                        <span className="me-1">{t('Documents')}</span>
                        <i
                            className="ri-information-line"
                            id="import-documents-helper"
                        />
                        <UncontrolledTooltip
                            target="import-documents-helper"
                            placement="right"
                            autohide={false}
                            innerClassName="text-start"
                            style={{ maxWidth: 360 }}
                        >
                            <p className="mb-2">
                                {t('Documents can contain technical documents, Declarations of Conformity, contracts, and similar files.')}
                            </p>
                            <p className="mb-2">
                                {t('Accepted formats are PDF, TXT, DOC, DOCX, XLS, XLSX, PPTX, JPG, JPEG, and PNG. ZIP files are not accepted.')}
                            </p>
                            <p className="mb-0">
                                {t('Start the file title with the SKU, creditor number, or debtor number. Otherwise the file is stored in Documents without a reference.')}
                            </p>
                        </UncontrolledTooltip>
                    </Label>
                </Col>
                <Col sm="12">
                    <div className="ai-onboarding-file-uploader w-100">
                        <Dropzone
                            onDrop={handleFilesDropped}
                            multiple
                            disabled={isUploading}
                            accept={ACCEPTED_FILE_TYPES}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <div className="dropzone tpdd p-3">
                                    <div
                                        className="dz-message needsclick"
                                        {...getRootProps()}
                                    >
                                        <input {...getInputProps()} />
                                        <button type="button" className="btn btn-outline">
                                            {t('Select files...')}
                                        </button>
                                        <h4>
                                            {t('Drop files here or click to upload')}
                                        </h4>
                                    </div>
                                </div>
                            )}
                        </Dropzone>
                        <small className="text-muted d-block mt-2">
                            {t('Accepted types: PDF, TXT, DOC, DOCX, XLS, XLSX, PPTX, JPG, JPEG, and PNG. Maximum size 20 MB each.')}
                        </small>
                        <div className="dropzone-previews tpdd">
                            {uploadingFiles.map((file, index) => renderFileCard(file, index, 'uploading'))}
                            {acceptedFiles.map((file, index) => renderFileCard(file, index, 'uploaded'))}
                            {rejectedFiles.map((file, index) => renderFileCard(file, index, 'failed'))}
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );

    const renderSummaryCards = () => {
        if (!summary) {
            return null;
        }

        return (
            <div className="document-linking-summary mb-4">
                {SUMMARY_CARDS.map((card) => {
                    const Icon = card.icon;

                    return (
                        <Card
                            key={card.key}
                            className="document-linking-summary__card h-100 mb-0"
                        >
                            <CardBody className="pb-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <p className="document-linking-summary__title mb-2">
                                            {t(card.title)}
                                        </p>
                                        <div className="document-linking-summary__value">
                                            {summary[card.key] ?? 0}
                                        </div>
                                    </div>
                                    <span
                                        className="document-linking-summary__icon"
                                        style={{
                                            backgroundColor: card.iconBackgroundColor,
                                            color: card.iconColor,
                                        }}
                                    >
                                        <Icon size={22} />
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    );
                })}
            </div>
        );
    };

    const renderResultStatus = (file) => {
        if (isProcessing && isLinkingStatus(file.status)) {
            return (
                <span className="d-inline-flex align-items-center text-primary">
                    <Spinner size="sm" color="primary" className="me-2" />
                    {t('Linking...')}
                </span>
            );
        }

        if (file.status === 'failed') {
            return <span className="badge bg-danger">{t('Failed')}</span>;
        }

        if (file.status === 'completed') {
            return <span className="badge bg-success">{t('Completed')}</span>;
        }

        return t(FILE_STATUS_LABELS[file.status] || file.status || '---');
    };

    const renderResultFiles = () => {
        const rows = resultFiles.length > 0
            ? resultFiles
            : acceptedFiles.map((file) => ({
                ...file,
                status: isProcessing ? 'processing' : file.status,
            }));

        return (
            <div className="document-linking-results table-responsive">
                <table className="table tpdd-table align-middle mb-0">
                    <thead className="thead-light">
                        <tr>
                            <th>{t('File name')}</th>
                            <th>{t('Status')}</th>
                            <th>{t('Supplier')}</th>
                            <th>{t('Product')}</th>
                            <th>{t('Match')}</th>
                            <th>{t('Message')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((file, index) => {
                            const isWarning = file.ambiguous || file.matchType === 'unlinked';
                            const isFailure = file.status === 'failed';
                            const waiting = isProcessing && isLinkingStatus(file.status);

                            return (
                                <tr key={fileKey(file, index)}>
                                    <td className={isFailure ? 'text-danger' : ''}>
                                        {file.filename}
                                    </td>
                                    <td>{renderResultStatus(file)}</td>
                                    <td>{file.supplierName || '---'}</td>
                                    <td>{file.productName || '---'}</td>
                                    <td className={isWarning ? 'text-warning' : ''}>
                                        {file.matchType
                                            ? t(MATCH_TYPE_LABELS[file.matchType] || file.matchType)
                                            : '---'}
                                    </td>
                                    <td className={isFailure ? 'text-danger' : (isWarning ? 'text-warning' : 'text-muted')}>
                                        {file.message || (waiting ? t('Matching this file...') : '---')}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <>
            <Alert
                icon={bottyCustomizeIcon}
                title="Upload Documents"
                message={(
                    <p className="mb-0">
                        {t('Add the documents you want to store with your third parties and products. These documents help us validate compliance, specifications and other important details during the import process.')}
                    </p>
                )}
            />

            <div className="ai-onboarding-content-body">
                {isStarting && (
                    <div className="text-center py-4">
                        <Spinner
                            color="primary"
                            size="sm"
                            className="me-2"
                        />
                        {t('Loading')}...
                    </div>
                )}

                {startError && (
                    <ReactstrapAlert color="danger">
                        <p className="mb-2">{startError}</p>
                        <Button
                            color="primary"
                            outline
                            type="button"
                            onClick={handleStartAgain}
                        >
                            {t('Start again')}
                        </Button>
                    </ReactstrapAlert>
                )}

                {isExpired && (
                    <ReactstrapAlert color="warning">
                        <p className="mb-2">
                            {t('This upload session expired. Start the upload again.')}
                        </p>
                        <Button
                            color="primary"
                            outline
                            type="button"
                            onClick={handleStartAgain}
                        >
                            {t('Start again')}
                        </Button>
                    </ReactstrapAlert>
                )}

                {canChooseFiles && renderUploader()}

                {canChooseFiles && acceptedFiles.length > 0 && (
                    <ReactstrapAlert color="info">
                        <h6 className="alert-heading mb-1">
                            {t('Files are ready to link')}
                        </h6>
                        <p className="mb-0">
                            {t('Start linking to match each uploaded file to a supplier or a product. You can still add files before you start.')}
                        </p>
                    </ReactstrapAlert>
                )}

                {isProcessing && (
                    <ReactstrapAlert color="info">
                        <h6 className="alert-heading mb-1">
                            <Spinner size="sm" className="me-2" />
                            {t('Linking...')}
                        </h6>
                        <p className="mb-0">
                            {t('Linking is running in the background. Each file is being matched to a supplier or a product.')}
                            {summary?.totalAccepted ? (
                                <> {summary.processed || 0} / {summary.totalAccepted}</>
                            ) : null}
                        </p>
                    </ReactstrapAlert>
                )}

                {isFinished && (
                    <ReactstrapAlert color={hasAttentionItems ? 'warning' : 'success'}>
                        <p className="mb-0">
                            {t(hasAttentionItems
                                ? 'Linking finished with some files that need attention. Review the results, then continue.'
                                : 'Linking finished. Review the results, then continue.')}
                        </p>
                    </ReactstrapAlert>
                )}

                {isFinalized && (
                    <>
                        {renderSummaryCards()}
                        {renderResultFiles()}
                    </>
                )}

                <div className="ai-onboarding-tab-actions">
                    <div className="d-flex justify-content-center align-items-center gap-2">
                        <Button
                            outline
                            color="secondary"
                            type="button"
                            onClick={onBackButtonClicked}
                        >
                            {t('Back')}
                        </Button>

                        {!isFinalized && (
                            <Button
                                color="secondary"
                                outline
                                type="button"
                                onClick={onNextButtonClicked}
                                disabled={isUploading || isFinalizing}
                            >
                                {t('Skip')}
                            </Button>
                        )}

                        {!isFinalized && acceptedFiles.length > 0 && (
                            <Button
                                color="primary"
                                type="button"
                                onClick={handleFinalize}
                                disabled={isUploading || isFinalizing}
                            >
                                {(isUploading || isFinalizing) && (
                                    <Spinner
                                        size="sm"
                                        color="white"
                                        className="me-1"
                                    />
                                )}
                                {t('Start linking')}
                            </Button>
                        )}

                        {isProcessing && (
                            <Button color="primary" type="button" disabled>
                                <Spinner size="sm" color="white" className="me-1" />
                                {t('Linking...')}
                            </Button>
                        )}

                        {isFinished && (
                            <Button
                                color="primary"
                                type="button"
                                onClick={onNextButtonClicked}
                            >
                                {t('Next')}
                            </Button>
                        )}
                    </div>

                    <Button
                        color="secondary"
                        outline
                        type="button"
                        onClick={onCancelButtonClicked}
                    >
                        {t('Cancel')}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default withNamespaces()(ImportDocuments);
